"use client";

// A kai game behind a React page: the game module downloads as soon as the page is
// interactive (dynamic import), and the page shows its own title screen meanwhile. The
// hook reports loading, and starts or continues the game when the page asks.
import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import type { BootOptions, GameHandle } from "@datagutt/kai";
export type GamePhase = "loading" | "ready" | "playing" | "failed";

/** A game's own start function (such as Fjord Town's startFjordTown). */
export type StartGame = (parent: HTMLElement, options: Pick<BootOptions, "onProgress" | "onReady">) => GameHandle;

/** `load` imports the game module; keep it stable (a module-level function). */
export function useKaiGame(load) {
  const $ = _c(7);
  const containerRef = useRef(null);
  const handleRef = useRef(null);
  const [phase, setPhase] = useState("loading");
  const [progress, setProgress] = useState(0);
  const [hasSave, setHasSave] = useState(false);
  let t0;
  let t1;
  if ($[0] !== load) {
    t0 = () => {
      let cancelled = false;
      load().then(startGame => {
        if (cancelled || !containerRef.current) {
          return;
        }
        const handle = startGame(containerRef.current, {
          onProgress: p => setProgress(p),
          onReady: () => setPhase(_temp)
        });
        handleRef.current = handle;
        setHasSave(handle.hasSave);
        if (handle.deepLinked) {
          setPhase("playing");
        }
      }).catch(err => {
        console.error("[game] failed to load", err);
        if (!cancelled) {
          setPhase("failed");
        }
      });
      return () => {
        cancelled = true;
        handleRef.current?.destroy();
        handleRef.current = null;
      };
    };
    t1 = [load];
    $[0] = load;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== hasSave || $[4] !== phase || $[5] !== progress) {
    t2 = {
      containerRef,
      phase,
      progress,
      hasSave,
      start(fresh) {
        containerRef.current?.focus({
          preventScroll: true
        });
        handleRef.current?.start({
          fresh
        });
        setPhase("playing");
      },
      playTitleMusic() {
        handleRef.current?.playTitleMusic();
      }
    };
    $[3] = hasSave;
    $[4] = phase;
    $[5] = progress;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function _temp(current) {
  return current === "loading" ? "ready" : current;
}